//CONSTANT
import { GET_APP_NAME_FOR_COLLECTION } from "../../app_constant";
import { getTodayLocal, getYesterdayLocal, getLocalDateString, getDeterministicIndexForDate } from "../utils/dates";

//SHARED
import { RESOLVE_TODAY_QOTD as coreResolveTodayQotd } from "@learnzapp/core/qotd";

//PACKAGES
import { firestore, auth } from "./config";
import { getEffectiveUid } from "./ghostUid";
import { GET_QUESTION_FROM_ID } from "./test";

async function GET_QUESTION_OF_THE_DAY() {
  try {
      const APP_NAME = await GET_APP_NAME_FOR_COLLECTION()
      const today = getTodayLocal();
      const result = await firestore().collection('apps').doc(APP_NAME).collection('qotd').doc(today).get()
      console.log('GET_QUESTION_OF_THE_DAY', result.exists, result.data())
      if (result.exists) {
          return { status: true, data: result.data(), error: '', isExist: true }
      }
      else {
          return { status: true, data: null, error: '', isExist: false }
      }
  }
  catch (error) {
      // Surface fetch failures (e.g. offline / uncached doc) instead of returning
      // undefined. Callers must be able to tell "network failed" apart from
      // "no doc exists" so they don't fabricate a random Question of the Day.
      console.log('GET_QUESTION_OF_THE_DAY error', error)
      return { status: false, data: null, error: error?.message ?? 'fetch_failed', isExist: false, isError: true }
  }
}

// Thin wrapper over the shared @learnzapp/core/qotd decision state machine.
// Core owns the pick + decision (so web and mobile can't drift); this app
// injects its own Firestore I/O, "today" (web's getTodayLocal), and the content
// lookup — adapting the hardcoded GET_QUESTION_FROM_ID to the DI'd
// getQuestionFromId(id) seam. Call sites (QOTDCard, QOTDAnswerScreen) unchanged.
async function RESOLVE_TODAY_QOTD({ questionsList }) {
  return coreResolveTodayQotd({
    questionsList,
    getQuestionFromId: (id) => GET_QUESTION_FROM_ID(questionsList, id),
    today: getTodayLocal(),
    fetchSharedDoc: () => GET_QUESTION_OF_THE_DAY(),
    createSharedDoc: (id) => UPDATE_QUESTION_OF_THE_DAY(id, questionsList),
  });
}

async function GET_QOTD_BY_DATE(date) {
  try {
      const APP_NAME = await GET_APP_NAME_FOR_COLLECTION()
      const result = await firestore().collection('apps').doc(APP_NAME).collection('qotd').doc(date).get()
      console.log('GET_QOTD_BY_DATE', date, result.exists, result.data())
      if (result.exists) {
          return { status: true, data: result.data(), error: '', isExist: true }
      }
      else {
          return { status: true, data: null, error: '', isExist: false }
      }
  }
  catch (error) {
      console.log('GET_QOTD_BY_DATE error', error)
      return { status: false, data: null, error: error.message }
  }
}

async function UPDATE_QUESTION_OF_THE_DAY(id, questionsList = null) {
  try {
      const APP_NAME = await GET_APP_NAME_FOR_COLLECTION()
      const today = getTodayLocal();
      
      // First, check if we need to seed QOTD history for the last 30 days
      if (questionsList && questionsList.length > 0) {
        await seedQOTDHistoryIfNeeded(APP_NAME, questionsList);
      }

      // Create today's question only if it doesn't already exist. A plain
      // .set() here would overwrite an existing question_id and wipe the
      // accumulated total_answered/correct_answers stats. Run in a transaction
      // so concurrent clients can't race; deterministic selection means they'd
      // write the same id anyway.
      const ref = firestore().collection('apps').doc(APP_NAME).collection('qotd').doc(today)
      await firestore().runTransaction(async (tx) => {
          const snap = await tx.get(ref)
          if (!snap.exists) {
              tx.set(ref, { question_id: id, date: today })
          }
      })
      return { status: true, data: null, error: '' }
  }
  catch (error) {
      console.log('UPDATE_QUESTION_OF_THE_DAY error', error)
      return { status: false, error: error.message }
  }
}

// One-time backfill of QOTD history, run ONLY on first-ever use of an app/exam
// (i.e. when the shared qotd collection is completely empty). This gives the
// history screen a populated 30-day grid for brand-new exams.
//
// It must NEVER run again afterwards and must NEVER touch today's doc — today's
// question is owned solely by the create-if-absent path in
// UPDATE_QUESTION_OF_THE_DAY. A previous rolling "< 30 days" gate caused this to
// re-run constantly and (via a stale offline cache) overwrite real days with
// seeded data, which corrupted the shared question/stats.
async function seedQOTDHistoryIfNeeded(APP_NAME, questionsList) {
  try {
    const collRef = firestore()
      .collection('apps')
      .doc(APP_NAME)
      .collection('qotd');

    // Seed only when no shared qotd data exists at all. Read from the server so
    // a stale/empty offline cache can't falsely re-trigger a backfill over real
    // data; if offline this throws and we skip seeding (handled by catch).
    const probe = await collRef.limit(1).get({ source: 'server' });
    if (!probe.empty) {
      return; // shared data already exists — never seed again
    }

    // Backfill PAST days only (i >= 1). Today (i = 0) is created separately by
    // the create-if-absent transaction; it must not be seeded.
    const batch = firestore().batch();
    for (let i = 29; i >= 1; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = getLocalDateString(date);

      // Same date -> question mapping the live create path uses, so seeded days
      // are consistent with how today is chosen.
      const index = getDeterministicIndexForDate(dateStr, questionsList.length);
      const selectedQuestion = questionsList[index];

      // Fabricated community performance data for the history difficulty badge
      // (written once, only on first-ever use).
      const totalAnswered = Math.floor(Math.random() * 50) + 10; // 10-59
      const correctAnswers = Math.floor(Math.random() * totalAnswered);

      batch.set(collRef.doc(dateStr), {
        question_id: selectedQuestion.id,
        date: dateStr,
        seeded: true,
        seeded_at: new Date().toISOString(),
        total_answered: totalAnswered,
        correct_answers: correctAnswers
      });
    }
    await batch.commit();
  } catch (error) {
    console.error('Error seeding QOTD history:', error);
    // Don't throw - seeding failure (e.g. offline) shouldn't block QOTD creation
  }
}

async function GET_QOTD_STATISTICS(date = null) {
  try {
    const APP_NAME = await GET_APP_NAME_FOR_COLLECTION();
    const targetDate = date || getTodayLocal();
    const result = await firestore().collection('apps').doc(APP_NAME).collection('qotd').doc(targetDate).get()
    return { status: true, data: result.data(), error: '' }
  }
  catch (error) {
    console.log('GET_QOTD_STATISTICS error', error)
  }
}

// Helper function to get user QOTD collection reference
const getUserQOTDCollection = async () => {
  const APP_NAME = await GET_APP_NAME_FOR_COLLECTION();
  return firestore()
    .collection("apps")
    .doc(APP_NAME)
    .collection("users")   
    .doc(getEffectiveUid())
    .collection("qotd");
};

async function UPDATE_USER_QOTD(qotdResult) {
  console.log('>>>>>>>>>>>>>>>>>>>>>>>> Calling UPDATE_USER_QOTD', qotdResult);
  try {
    const today = getTodayLocal();
    const yesterday = getYesterdayLocal();
    
    const qotdCollection = await getUserQOTDCollection();
    
    // Save today's QOTD response as a separate document
    await qotdCollection.doc(today).set({
      date: today,
      isCorrect: qotdResult.isCorrect,
      timeSpent: qotdResult.timeSpent || 0,
      answered_at: new Date().toISOString(),
      ...qotdResult // Include any additional data
    });
    
    // Get or create summary document
    const summaryDoc = await qotdCollection.doc("summary").get();
    const currentSummary = summaryDoc.exists ? summaryDoc.data() : {
      current_streak: 0,
      best_streak: 0,
      total_answered: 0,
      total_correct: 0,
      last_qotd_date: null
    };
    
    // Check if user answered yesterday to calculate streak
    const yesterdayDoc = await qotdCollection.doc(yesterday).get();
    const answeredYesterday = yesterdayDoc.exists;
    
    let newStreak;
    if (answeredYesterday) {
      newStreak = currentSummary.current_streak + 1;
    } else {
      newStreak = 1; // Reset streak to 1 as they answered today
    }
    
    // Update best streak if current streak is greater
    const newBestStreak = Math.max(currentSummary.best_streak, newStreak);
    
    // Update summary
    const newSummary = {
      current_streak: newStreak,
      best_streak: newBestStreak,
      total_answered: currentSummary.total_answered + 1,
      total_correct: currentSummary.total_correct + (qotdResult.isCorrect ? 1 : 0),
      last_qotd_date: today
    };
    
    await qotdCollection.doc("summary").set(newSummary);
    
    // Update global qotd statistics
    const APP_NAME = await GET_APP_NAME_FOR_COLLECTION();
    const questionOfDayRef = firestore()
      .collection('apps')
      .doc(APP_NAME)
      .collection('qotd')
      .doc(today);
    
    // Use atomic operations to update statistics
    await questionOfDayRef.update({
      total_answered: firestore.FieldValue.increment(1),
      correct_answers: firestore.FieldValue.increment(qotdResult.isCorrect ? 1 : 0)
    });
    
    console.log('>>>>>>>>>>>>>>>>>>>>>>>> UPDATE_USER_QOTD completed', newSummary);
    return { status: true, error: "" };
  } catch (error) {
    console.log('>>>>>>>>>>>>>>>>>>>>>>>> Error in UPDATE_USER_QOTD', error);
    return { status: false, error: error.message };
  }
}

// Function to get QOTD history for the last 30 days
async function GET_QOTD_HISTORY() {
  try {
    const APP_NAME = await GET_APP_NAME_FOR_COLLECTION();
    
    // Get QOTD data for the last 30 days using local timezone
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const thirtyDaysAgoStr = getLocalDateString(thirtyDaysAgo);
    
    const result = await firestore()
      .collection('apps')
      .doc(APP_NAME)
      .collection('qotd')
      .where('date', '>=', thirtyDaysAgoStr)
      .orderBy('date', 'desc')
      .get();
    
    const history = [];
    result.docs.forEach(doc => {
      const data = doc.data();
      history.push({
        date: data.date,
        question_id: data.question_id,
        seeded: data.seeded || false,
        total_answered: data.total_answered || 0,
        correct_answers: data.correct_answers || 0
      });
    });
    
    return { status: true, data: history, error: '' };
  } catch (error) {
    console.error('GET_QOTD_HISTORY error:', error);
    return { status: false, data: [], error: error.message };
  }
}

// Function to get user's QOTD summary
async function GET_USER_QOTD_SUMMARY() {
  try {
    const qotdCollection = await getUserQOTDCollection();
    const summaryDoc = await qotdCollection.doc("summary").get();
    
    const summary = summaryDoc.exists ? summaryDoc.data() : {
      current_streak: 0,
      best_streak: 0,
      total_answered: 0,
      total_correct: 0,
      last_qotd_date: null
    };
    
    return { status: true, data: summary, error: '' };
  } catch (error) {
    console.error('GET_USER_QOTD_SUMMARY error:', error);
    return { status: false, data: null, error: error.message };
  }
}

// Function to get user's QOTD response for a specific date
async function GET_USER_QOTD_RESPONSE(date) {
  try {
    const qotdCollection = await getUserQOTDCollection();
    const responseDoc = await qotdCollection.doc(date).get();
    
    if (responseDoc.exists) {
      return { status: true, data: responseDoc.data(), error: '' };
    } else {
      return { status: true, data: null, error: '', exists: false };
    }
  } catch (error) {
    console.error('GET_USER_QOTD_RESPONSE error:', error);
    return { status: false, data: null, error: error.message };
  }
}

// Function to get user's QOTD responses for a date range
async function GET_USER_QOTD_RESPONSES(startDate, endDate) {
  try {
    const qotdCollection = await getUserQOTDCollection();
    
    const result = await qotdCollection
      .where('date', '>=', startDate)
      .where('date', '<=', endDate)
      .orderBy('date', 'desc')
      .get();
    
    const responses = [];
    result.docs.forEach(doc => {
      responses.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return { status: true, data: responses, error: '' };
  } catch (error) {
    console.error('GET_USER_QOTD_RESPONSES error:', error);
    return { status: false, data: [], error: error.message };
  }
}

// Function to get and update the current QOTD streak (legacy function for backward compatibility)
async function getStreak(profile = {}) {
  try {
    const summary = await GET_USER_QOTD_SUMMARY();
    if (summary.status) {
      return summary.data.current_streak;
    }
    return 0;
  } catch (error) {
    console.error('getStreak error:', error);
    return 0;
  }
}

export {
  GET_QUESTION_OF_THE_DAY,
  RESOLVE_TODAY_QOTD,
  GET_QOTD_BY_DATE,
  UPDATE_QUESTION_OF_THE_DAY,
  UPDATE_USER_QOTD,
  GET_QOTD_STATISTICS,
  GET_QOTD_HISTORY,
  GET_USER_QOTD_SUMMARY,
  GET_USER_QOTD_RESPONSE,
  GET_USER_QOTD_RESPONSES,
  getStreak
};
